import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useGuardian } from './GuardianContext';
import supabase from '../supabase/supabase';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSubscriptionData();
    } else {
      setLoading(false);
    }
  }, [user, getActiveUserId()]);

  // Auto-refresh subscription data every minute to keep days countdown accurate
  useEffect(() => {
    if (user && subscriptionData) {
      const interval = setInterval(() => {
        loadSubscriptionData();
      }, 60000); // Refresh every minute

      return () => clearInterval(interval);
    }
  }, [user, subscriptionData]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();

      const { data, error } = await supabase
        .from('profiles')
        .select(
          'trial_started_at, trial_ends_at, subscription_status, subscription_plan, subscription_started_at, subscription_ends_at'
        )
        .eq('user_id', activeUserId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSubscriptionData(data);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isTrialActive = () => {
    if (!subscriptionData) return false;
    const now = new Date();
    const trialEnd = new Date(subscriptionData.trial_ends_at);
    return subscriptionData.subscription_status === 'trial' && now < trialEnd;
  };

  const isTrialExpired = () => {
    if (!subscriptionData) return false;
    const now = new Date();
    const trialEnd = new Date(subscriptionData.trial_ends_at);
    return subscriptionData.subscription_status === 'trial' && now >= trialEnd;
  };

  const hasActiveSubscription = () => {
    if (!subscriptionData) return false;
    return subscriptionData.subscription_status === 'active';
  };

  const canAccessPremiumFeatures = () => {
    return isTrialActive() || hasActiveSubscription();
  };

  const getDaysLeftInTrial = () => {
    if (!subscriptionData || !isTrialActive()) return 0;
    
    const now = new Date();
    const trialEnd = new Date(subscriptionData.trial_ends_at);
    
    // Calculate difference in milliseconds
    const diffTime = trialEnd - now;
    
    // Convert to days and round up (so 23 hours = 1 day)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const getHoursLeftInTrial = () => {
    if (!subscriptionData || !isTrialActive()) return 0;
    
    const now = new Date();
    const trialEnd = new Date(subscriptionData.trial_ends_at);
    
    // Calculate difference in milliseconds
    const diffTime = trialEnd - now;
    
    // Convert to hours
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    return Math.max(0, diffHours);
  };

  const upgradeToPro = async (plan) => {
    try {
      const activeUserId = getActiveUserId();
      const now = new Date();
      const endsAt =
        plan === 'pro_monthly'
          ? new Date(now.setMonth(now.getMonth() + 1))
          : new Date(now.setFullYear(now.getFullYear() + 1));

      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_status: 'active',
          subscription_plan: plan,
          subscription_started_at: new Date().toISOString(),
          subscription_ends_at: endsAt.toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', activeUserId);

      if (error) throw error;

      await loadSubscriptionData();
      return { success: true };
    } catch (error) {
      console.error('Error upgrading to Pro:', error);
      throw error;
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionData,
        loading,
        isTrialActive,
        isTrialExpired,
        hasActiveSubscription,
        canAccessPremiumFeatures,
        getDaysLeftInTrial,
        getHoursLeftInTrial,
        upgradeToPro,
        refreshSubscription: loadSubscriptionData,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};